/**
 * Copyright 2023 LiveKit, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  LiveKitRoom,
  RoomAudioRenderer,
  TrackReference,
  useRoomContext,
  useTracks,
} from '@livekit/components-react';
import EgressHelper from '@livekit/egress-sdk';
import { ConnectionState, RoomEvent, Track } from 'livekit-client';
import { ReactElement, SyntheticEvent, useEffect, useState } from 'react';
import SingleSpeakerLayout from './SingleSpeakerLayout';
import SpeakerLayout from './SpeakerLayout';
import { ParticipantInfo } from 'livekit-client/dist/src/proto/livekit_models_pb';
import { GridLayout } from './components';
import { ParticipantTile } from './components/Participant/ParticipantTitle';
import WhiteBoard from './components/whiteBoard/whiteBoard';
import SlidesPresentation from './components/slides/SlidesPresentation';
import DocPresentation, { PublishedPresentationData } from './components/presentations/DocPresentation';
import { Transcription } from './components/Transcription';

interface RoomPageProps {
  url: string;
  token: string;
  layout: string;
}

export default function RoomPage({ url, token, layout }: RoomPageProps) {
  const [error, setError] = useState<Error>();
  if (!url || !token) {
    return <div className="error">missing required params url and token</div>;
  }

  return (
    <LiveKitRoom serverUrl={url} token={token} onError={setError}>
      {error ? <div className="error">{error.message}</div> : <CompositeTemplate layout={layout} />}
    </LiveKitRoom>
  );
}

interface CompositeTemplateProps {
  layout: string;
}

function CompositeTemplate({ layout: initialLayout }: CompositeTemplateProps) {
  interface AdminVisibility {
    screenShare: {
      showAdmin: boolean;
      identity: string;
      source: string;
    };
    cameraSource: {
      showAdmin: boolean;
      identity: string;
      source: string;
    };
  }
  interface ParticipantVisible {
    sid: string;
    source: string;
  }
  interface DiapositiveData {
    id?: number;
    index: string;
    loop: string;
    name: string;
    slideUrl: string;
    timestamp: string;
  }
  const room = useRoomContext();
  const [layout, setLayout] = useState(initialLayout);
  const [hasScreenShare, setHasScreenShare] = useState(false);
  const [selectedCard, setSelectedCard] = useState('grid');
  const [AdminObjectInMetadata, setAdminObjectInMetadata] = useState<AdminVisibility>();
  const [showAdmin, setShowAdmin] = useState(true);
  const [ParticipantVisible, setParticipantVisible] = useState<ParticipantVisible[]>([]);
const [whiteBoardVisible , setWhiteBoardVisible] = useState(false)
const [whiteBoardValue , setWhiteBoardValue]= useState('')
	const [EgressValue, setEgressValue] = useState("");
  const [publishedSlides, setPublishedSlides] = useState<DiapositiveData[]>([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [publishedPresentation, setPublishedPresentation] = useState<PublishedPresentationData | null>(null);

  const AdminEmail = AdminObjectInMetadata?.cameraSource?.identity;

  const screenshareTracks = useTracks([Track.Source.ScreenShare], {
    onlySubscribed: true,
  });

  const handleSlidesImageError = (event: SyntheticEvent<HTMLImageElement, Event>) => {
    event.currentTarget.src = '/assets/img/defaultImage.png';
    event.currentTarget.alt = 'slide_default';
  };

  const clearPublishedSlides = () => {
    setPublishedSlides([]);
    setCurrentSlideIndex(0);
  };

  useEffect(() => {
    if (room) {
      EgressHelper.setRoom(room);

      // Egress layout can change on the fly, we can react to the new layout
      // here.
      EgressHelper.onLayoutChanged((newLayout) => {
        setLayout(newLayout);
      });

      // start recording when there's already a track published
      let hasTrack = false;
      for (const p of Array.from(room.participants.values())) {
        if (p.tracks.size > 0) {
          hasTrack = true;
          break;
        }
      }

      if (hasTrack) {
        EgressHelper.startRecording();
      } else {
        room.once(RoomEvent.TrackSubscribed, () => EgressHelper.startRecording());
      }
    }
  }, [room]);
  useEffect(() => {
    if (room.metadata) {
      const remoteMetaData = JSON.parse(room.metadata);

      setSelectedCard(remoteMetaData.layout?.name ?? '');
      setAdminObjectInMetadata(remoteMetaData?.adminVisibility ?? {});
      setShowAdmin(remoteMetaData?.adminVisibility?.cameraSource.showAdmin ?? true);
      setParticipantVisible(remoteMetaData.ParticipantVisibilitiy ?? []);
      setWhiteBoardVisible(remoteMetaData.whiteBoardVisible ?? false)
      setEgressValue(remoteMetaData.diffusionEggressId?? "")
      setWhiteBoardValue(remoteMetaData.fluxPresented?? "")

    }
  }, [room.metadata]);
  useEffect(() => {
    if (!room?.metadata) {
      setPublishedSlides([]);
      setCurrentSlideIndex(0);
      return;
    }
    try {
      const remoteMetaData = JSON.parse(room.metadata);
      const remoteSlides = Array.isArray(remoteMetaData?.slidesCollection)
        ? remoteMetaData.slidesCollection
        : [];
      setPublishedSlides(remoteSlides);
      setCurrentSlideIndex((prev) => {
        if (remoteSlides.length === 0) {
          return 0;
        }
        return prev >= remoteSlides.length ? 0 : prev;
      });
    } catch (error) {
      console.error('Failed to parse slides for presentation', error);
      setPublishedSlides([]);
      setCurrentSlideIndex(0);
    }
  }, [room?.metadata]);
  useEffect(() => {
    if (!room?.metadata) {
      setPublishedPresentation(null);
      return;
    }
    try {
      const remoteMetaData = JSON.parse(room.metadata);
      const remotePres = remoteMetaData?.publishedPresentation ?? null;
      setPublishedPresentation(remotePres);
    } catch (error) {
      console.error('Failed to parse presentation metadata', error);
      setPublishedPresentation(null);
    }
  }, [room?.metadata]);
  useEffect(() => {
    if (publishedSlides.length <= 1) {
      return;
    }

    const rotationTimer = window.setInterval(() => {
      setCurrentSlideIndex((prev) => {
        const nextIndex = prev + 1;
        return nextIndex >= publishedSlides.length ? 0 : nextIndex;
      });
    }, 5000);

    return () => {
      window.clearInterval(rotationTimer);
    };
  }, [publishedSlides]);
  useEffect(() => {
    if (screenshareTracks.length > 0 && screenshareTracks[0].publication) {
      setHasScreenShare(true);
    } else {
      setHasScreenShare(false);
    }
  }, [screenshareTracks]);

  const allTracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    {
      onlySubscribed: true,
    },
  );
  let tracks = allTracks?.filter(track => track.participant.name || track.participant.identity === "Media") as TrackReference[];


  const carouselTracks = tracks.filter((track) => {
    if (
      track.participant.identity === AdminEmail &&
      showAdmin === false &&
      track.source === AdminObjectInMetadata?.cameraSource?.source
    ) {
      return false;
    } else if (
      track.participant.identity === AdminEmail &&
      AdminObjectInMetadata?.screenShare?.showAdmin === false &&
      track?.source === AdminObjectInMetadata?.screenShare?.source
    ) {
      return false;
    } else {
      return tracks;
    }
  });

	const filteredTracks = carouselTracks.filter(track => {

		
		return (
      track.participant.identity !== room.localParticipant.identity &&
			track.participant.permissions?.canPublishData === true &&
		(	ParticipantVisible.some(participant => participant.sid === track.participant.sid && participant.source === track.source) || track.participant.identity==="shared-url" || track.participant.identity==="remote-obs" || track.source==="screen_share" )
		);
	});	

  let interfaceStyle = 'dark';
  if (layout?.endsWith('-light')) {
    interfaceStyle = 'light';
  }

  let containerClass = 'roomContainer';
  if (interfaceStyle) {
    containerClass += ` ${interfaceStyle}`;
  }

  // determine layout to use
  let main: ReactElement = <></>;
  let effectiveLayout = layout;
  if (hasScreenShare && selectedCard === 'grid') {
    effectiveLayout = layout.replace('grid', 'speaker');
  }
  if (room.state !== ConnectionState.Disconnected) {
    if (selectedCard === 'speaker') {
      main = <SpeakerLayout tracks={filteredTracks} />;
    } else if (selectedCard === 'single-speaker') {
      main = <SingleSpeakerLayout tracks={filteredTracks} />;
    } else {
      main = (
        <GridLayout tracks={filteredTracks}>
          <ParticipantTile />
        </GridLayout>
      );
    }
  }

  const hasActiveSlide = publishedSlides.length > 0 && Boolean(publishedSlides[currentSlideIndex]);
  const activeSlide = hasActiveSlide ? publishedSlides[currentSlideIndex] : undefined;

  return (
    <div className={containerClass}>
      {publishedPresentation ? (
        <DocPresentation presentation={publishedPresentation} />
      ) : hasActiveSlide && activeSlide ? (
        <SlidesPresentation
          publishedSlides={publishedSlides}
          currentSlideIndex={currentSlideIndex}
          activeSlide={activeSlide}
          onClose={clearPublishedSlides}
          onImageError={handleSlidesImageError}
        />
      ) : (
        main
      )}
      {whiteBoardValue ==EgressValue && 
        <WhiteBoard
         isOpen={whiteBoardVisible}
     
    />
      
      }
      <Transcription />
      <RoomAudioRenderer />
    </div>
  );
}
